import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const OPENAI_SETTINGS_COLLECTION = 'openai_settings';

export const saveOpenAISettings = async (userId, settings) => {
  try {
    if (!userId) throw new Error('User ID is required');
    
    console.log('Saving OpenAI settings for user:', userId);
    const settingsRef = doc(db, OPENAI_SETTINGS_COLLECTION, userId);
    await setDoc(settingsRef, {
      ...settings,
      updatedAt: new Date().toISOString()
    });

    console.log('Successfully saved OpenAI settings');
    return settings;
  } catch (error) {
    console.error('Error saving OpenAI settings:', error);
    throw error;
  }
};

export const getOpenAISettings = async (userId) => {
  try {
    if (!userId) {
      console.error('getOpenAISettings called with no userId');
      return null;
    }
    
    console.log('Getting OpenAI settings for user:', userId);
    const settingsRef = doc(db, OPENAI_SETTINGS_COLLECTION, userId);
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      console.log('Found OpenAI settings:', data);
      return data;
    } else {
      console.log('No OpenAI settings found for user:', userId);
      // Return null to trigger default settings creation
      return null;
    }
  } catch (error) {
    console.error('Error getting OpenAI settings:', error);
    throw error;
  }
};

export const fetchOpenAIModels = async (apiKey) => {
  try {
    console.log('OpenAI Request:', {
      url: 'https://api.openai.com/v1/models',
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    const response = await fetch('https://api.openai.com/v1/models', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error('OpenAI Error Response:', {
        status: response.status,
        statusText: response.statusText,
        data
      });
      throw new Error(data.error?.message || `OpenAI API error: ${response.status} ${response.statusText}`);
    }

    console.log('OpenAI Response:', data);
    return data.data
      .filter(model => model.id.includes('gpt')) // Only show GPT models
      .sort((a, b) => b.created - a.created); // Sort by newest first
  } catch (error) {
    console.error('Error fetching OpenAI models:', {
      error: error.message,
      stack: error.stack
    });
    throw error;
  }
};

export const createCompletion = async (apiKey, { model, messages, temperature, max_tokens }) => {
  try {
    console.log('OpenAI Request:', {
      url: '/api/openai/completion',
      model,
      messages,
      temperature,
      max_tokens
    });

    const response = await fetch('/api/openai/completion', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-OpenAI-Key': apiKey
      },
      body: JSON.stringify({
        model,
        messages,
        temperature: temperature || 0.7,
        max_tokens: max_tokens || 2000,
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error('OpenAI Error Response:', {
        status: response.status,
        statusText: response.statusText,
        data
      });
      throw new Error(data.error?.message || `OpenAI API error: ${response.status} ${response.statusText}`);
    }

    console.log('OpenAI Response:', data);
    return data;
  } catch (error) {
    console.error('Error creating completion:', {
      error: error.message,
      stack: error.stack
    });
    throw error;
  }
};

export const processTemplateWithOpenAI = async (apiKey, template, productData) => {
  try {
    console.log('Processing template with OpenAI:', {
      template: template.name,
      model: template.model,
      productId: productData.id
    });

    // Prepare messages for OpenAI
    const messages = [
      {
        role: 'system',
        content: `You are a product content generator for Shopify. Your task is to generate content based on the provided product data and template instructions. 
        Respond ONLY with a valid JSON object containing the requested fields. Do not include any explanations or additional text outside of the JSON.`
      },
      {
        role: 'user',
        content: `${template.prompt || ''}
        
        Product Data:
        ${JSON.stringify(productData, null, 2)}
        
        Please generate content for the following fields: ${template.outputFields.join(', ')}
        
        Respond with a valid JSON object containing only the requested fields.`
      }
    ];

    // Call OpenAI API
    const response = await createCompletion(apiKey, {
      model: template.model || 'gpt-4o-mini',
      messages,
      temperature: template.temperature || 0.7,
      max_tokens: template.max_tokens || 2000
    });

    // Parse the response
    const content = response.choices[0].message.content;
    
    try {
      // Try to parse the JSON
      const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/) || 
                       content.match(/```\n([\s\S]*?)\n```/) || 
                       content.match(/{[\s\S]*?}/);
      
      if (jsonMatch) {
        // Parse the JSON
        const result = JSON.parse(jsonMatch[0].replace(/```json\n|```\n|```/g, ''));
        console.log('Parsed template result:', result);
        return result;
      } else {
        // Try to parse the whole content as JSON
        const result = JSON.parse(content);
        console.log('Parsed template result:', result);
        return result;
      }
    } catch (error) {
      console.error('Error parsing template result:', {
        error: error.message,
        content
      });
      throw new Error(`Failed to parse template result: ${error.message}`);
    }
  } catch (error) {
    console.error('Error processing template with OpenAI:', {
      error: error.message,
      stack: error.stack
    });
    throw error;
  }
};
